
import * as React from "react"
import { CheckIcon, SearchIcon } from "lucide-react"
import { cn } from "@/lib/utils"

export interface OptionsListItem {
  value: string
  label: string
  disabled?: boolean
}

export interface OptionsListGroup {
  label: string
  options: OptionsListItem[]
}

interface OptionsListProps {
  options?: OptionsListItem[]
  groups?: OptionsListGroup[]
  selectedValues: string[]
  onSelect: (value: string) => void
  onEscape?: () => void
  searchPlaceholder?: string
  emptyText?: string
  maxHeight?: number
}

export function OptionsList({
  options,
  groups,
  selectedValues,
  onSelect,
  onEscape,
  searchPlaceholder = "Search...",
  emptyText = "No results found.",
  maxHeight = 240,
}: OptionsListProps) {
  const [search, setSearch] = React.useState("")
  const [highlightIndex, setHighlightIndex] = React.useState(0)
  const listRef = React.useRef<HTMLDivElement>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)

  // Only auto-focus on devices with a fine pointer (mouse/trackpad)
  // to avoid popping the mobile virtual keyboard
  React.useEffect(() => {
    if (window.matchMedia("(pointer: fine)").matches) {
      inputRef.current?.focus({ preventScroll: true })
    }
  }, [])

  React.useEffect(() => {
    const handler = (e: Event) => {
      const el = listRef.current
      if (el && el.contains(e.target as Node)) e.stopPropagation()
    }
    window.addEventListener("wheel", handler, { capture: true })
    window.addEventListener("touchmove", handler, { capture: true })
    return () => {
      window.removeEventListener("wheel", handler, { capture: true })
      window.removeEventListener("touchmove", handler, { capture: true })
    }
  }, [])

  const allOptions = React.useMemo(() => {
    if (options) return options
    if (groups) return groups.flatMap((g) => g.options)
    return []
  }, [options, groups])

  const filtered = React.useMemo(() => {
    const q = search.toLowerCase().trim()
    if (!q) return allOptions
    return allOptions.filter((o) => o.label.toLowerCase().includes(q))
  }, [allOptions, search])

  const filteredGroups = React.useMemo(() => {
    if (!groups) return undefined
    const q = search.toLowerCase().trim()
    if (!q) return groups
    return groups
      .map((g) => ({
        ...g,
        options: g.options.filter((o) => o.label.toLowerCase().includes(q)),
      }))
      .filter((g) => g.options.length > 0)
  }, [groups, search])

  React.useEffect(() => {
    setHighlightIndex(0)
  }, [search])

  React.useEffect(() => {
    const list = listRef.current
    if (!list) return
    const el = list.querySelector(`[data-index="${highlightIndex}"]`)
    if (el) el.scrollIntoView({ block: "nearest" })
  }, [highlightIndex])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const enabledItems = filtered.filter((o) => !o.disabled)
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setHighlightIndex((i) => Math.min(i + 1, enabledItems.length - 1))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setHighlightIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === "Enter") {
      e.preventDefault()
      const item = enabledItems[highlightIndex]
      if (item) onSelect(item.value)
    } else if (e.key === "Escape") {
      onEscape?.()
    }
  }

  const renderOption = (option: OptionsListItem, idx: number) => (
    <div
      key={option.value}
      data-index={idx}
      role="option"
      aria-selected={selectedValues.includes(option.value)}
      aria-disabled={option.disabled}
      className={cn(
        "relative flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm select-none",
        option.disabled && "pointer-events-none opacity-50",
        highlightIndex === idx && "bg-accent text-accent-foreground",
      )}
      onMouseEnter={() => !option.disabled && setHighlightIndex(idx)}
      onClick={() => !option.disabled && onSelect(option.value)}
    >
      <CheckIcon
        className={cn(
          "size-4 shrink-0",
          selectedValues.includes(option.value) ? "opacity-100" : "opacity-0"
        )}
      />
      {option.label}
    </div>
  )

  let flatIdx = 0

  return (
    <>
      <div className="flex h-9 items-center gap-2 border-b px-3">
        <SearchIcon className="size-4 shrink-0 opacity-50" />
        <input
          ref={inputRef}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={searchPlaceholder}
          className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
        />
      </div>
      <div
        ref={listRef}
        role="listbox"
        style={{ maxHeight, overflowY: "auto", overscrollBehavior: "contain", WebkitOverflowScrolling: "touch" }}
        className="p-1"
      >
        {filteredGroups
          ? (() => {
              flatIdx = 0
              return filteredGroups.map((group, gi) => (
                <div key={`${group.label}-${gi}`} role="presentation">
                  <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                    {group.label}
                  </div>
                  {group.options.map((option) => {
                    const idx = flatIdx++
                    return renderOption(option, idx)
                  })}
                </div>
              ))
            })()
          : filtered.length > 0
            ? filtered.map((option, idx) => renderOption(option, idx))
            : (
              <div className="py-6 text-center text-sm text-muted-foreground">
                {emptyText}
              </div>
            )}
      </div>
    </>
  )
}
